import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import {
  NavContentBlueprint,
  NavContentComponent,
  NavContentComponentProps,
  NavContentNavItem,
} from '@backstage/plugin-app-react';
import { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import { SheriffAdminNavGate } from '@internal/backstage-plugin-sheriff-admin';
import { AdAmsNavGate } from '@internal/backstage-plugin-ad-ams';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FeedbackIcon from '@material-ui/icons/Feedback';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { Settings as SidebarSettings } from '@backstage/plugin-user-settings';
import { BugsyThreadsPanel } from '@internal/backstage-plugin-bugsy';
import { vendorPerformanceAccessPermission } from '@internal/backstage-plugin-vendor-performance';
import { ConditionalPermission } from '../../components/permissions/ConditionalPermission';

const SURVEY_URL =
  'https://vwg.vote/survey-sessions/73657373696f6e2e3637333631.e9340c01c5279c416a2e3f4f78d0b1160c640ef4ab6a39e4849f27a03c085908';

const useBugsyNavStyles = makeStyles(theme => ({
  row: {
    position: 'relative',
  },
  toggle: {
    position: 'absolute',
    right: theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
    color: theme.palette.navigation?.color ?? '#FFFFFF',
    padding: theme.spacing(0.5),
  },
}));

/**
 * The "Bugsy AI" nav row plus its inline, click-to-expand thread list.
 *
 * Replaces the previous hover flyout (`SidebarSubmenu`) per direct user
 * feedback -- the list now renders beneath the nav item, in the sidebar's
 * own column, instead of sliding out as a drawer.
 *
 * The label link (`SidebarItem`) only navigates to `/bugsy`; a separate
 * chevron toggles the list. Combining both on one click would mean
 * navigating to a thread and clicking "Bugsy AI" again (e.g. to scroll back
 * up) also collapses the list out from under you -- surprising for someone
 * who only wanted to go back to the chat. A dedicated toggle keeps
 * navigation and disclosure independent.
 *
 * `expanded` is plain component state, not persisted to storage: the
 * sidebar tree is mounted once for the app's lifetime and does not remount
 * between routes, so the state (and the open/closed choice the user made)
 * survives navigating to a thread and back. It does not survive a full page
 * reload, which re-mounts everything -- on reload, `expanded` is
 * recomputed once from the current URL, so landing directly on `/bugsy`
 * still opens the list.
 *
 * `BugsyThreadsPanel` (from the plugin) is only ever mounted while
 * `expanded` is true and the sidebar itself is open (not the collapsed icon
 * rail, per `useSidebarOpenState`) -- that conditional mount is the fetch
 * guard now that hover no longer provides one for free.
 */
function BugsyNavItem({ item, text }: { item: NavContentNavItem; text: string }) {
  const classes = useBugsyNavStyles();
  const location = useLocation();
  const { isOpen } = useSidebarOpenState();
  const [expanded, setExpanded] = useState(
    () => location.pathname === item.href || location.pathname.startsWith(`${item.href}/`),
  );

  return (
    <>
      <div className={classes.row}>
        <SidebarItem icon={() => item.icon} to={item.href} text={text} />
        {isOpen && (
          <IconButton
            size="small"
            className={classes.toggle}
            data-testid="bugsy-threads-toggle"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${text} threads`}
            aria-expanded={expanded}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              setExpanded(current => !current);
            }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </div>
      {isOpen && expanded && <BugsyThreadsPanel />}
    </>
  );
}

/**
 * The nav content, extracted from the blueprint so it can be rendered
 * directly in a test -- `NavContentBlueprint.make` only produces an
 * extension descriptor, not a callable component.
 */
export const renderSidebarContent: NavContentComponent = ({
  navItems,
}: NavContentComponentProps) => {
  const getSidebarTitle = (item: { href: string; title: string }) => {
    if (item.href === '/catalog') {
      return 'Catalog';
    }

    if (item.href === '/api-docs') {
      return 'APIs';
    }

    if (item.href === '/docs') {
      return 'Docs';
    }

    if (item.href === '/create') {
      return 'Software Templates';
    }

    if (item.href === '/vwtech-libs') {
      return 'Landscape'
    }

    if (item.href === '/bugsy') {
      return 'Bugsy AI'
    }

    if (item.href === '/tech-radar') {
      return 'Tech Radar';
    }

    if (item.href === '/vendor-performance') {
      return 'Vendor Performance';
    }

    return item.title;
  };

  const nav = navItems.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={getSidebarTitle(item)} />
  ));

  const itemByHref = new Map(navItems.rest().map(item => [item.href, item.node.spec.id]));
  const takeByHref = (href: string) => {
    const id = itemByHref.get(href);
    return id ? nav.take(id) : null;
  };
  // Raw (un-rendered) item, for the one entry that needs a custom render --
  // `nav.take()` hands back a pre-built `SidebarItem` with no room to nest
  // the chevron toggle and inline thread list next to and below it. Taken
  // from `navItems` directly rather than `nav`, but both share the same
  // underlying collection, so this still removes it from `nav.rest()` below.
  const takeRawByHref = (href: string) => {
    const id = itemByHref.get(href);
    return id ? navItems.take(id) : undefined;
  };

  takeByHref('/search');
  takeByHref('/settings');
  takeByHref('/kubernetes');
  takeByHref('/catalog-import');
  takeByHref('/catalog-graph'); // hidden from nav until graph page is properly wired up

  const bugsyItem = takeRawByHref('/bugsy');
  const sheriffAdminItem = takeByHref('/sheriff-admin');
  const adAmsItem = takeByHref('/ad-ams');

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarScrollWrapper>
          {takeByHref('/catalog')}
          {takeByHref('/api-docs')}
          {takeByHref('/docs')}
          {takeByHref('/create')}
          {takeByHref('/arch-drawio')}
          {takeByHref('/vwtech-libs')}
          {bugsyItem && (
            <BugsyNavItem
              key={bugsyItem.node.spec.id}
              item={bugsyItem}
              text={getSidebarTitle(bugsyItem)}
            />
          )}
          {takeByHref('/tech-radar')}
          <ConditionalPermission permission={vendorPerformanceAccessPermission}>
            {takeByHref('/vendor-performance')}
          </ConditionalPermission>
          <AdAmsNavGate>{adAmsItem}</AdAmsNavGate>
          {nav.rest({ sortBy: 'title' })}

          <SheriffAdminNavGate>{sheriffAdminItem}</SheriffAdminNavGate>
          {/* SidebarItem's `to` goes through react-router, which can't leave the app. */}
          <SidebarItem
            icon={FeedbackIcon}
            text="Feedback"
            onClick={() => window.open(SURVEY_URL, '_blank', 'noopener')}
          />
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarSettings />
    </Sidebar>
  );
};

export const SidebarContent: ExtensionDefinition = NavContentBlueprint.make({
  params: {
    component: renderSidebarContent,
  },
});
